import { get, post } from '../../../services/api';
import {
  BillingCompany,
  BillingOrder,
  BillingItem,
  Invoice
} from '../context/BillingInvoicingContext';
import { API_CONFIG } from '../../../config/api.config';

export async function fetchCompanies(): Promise<BillingCompany[]> {
  return get<BillingCompany[]>('/rtus');
}

export async function fetchOrders(rtuId: string): Promise<BillingOrder[]> {
  return get<BillingOrder[]>(`/v1/orders?rtuId=${rtuId}`);
}

export async function generateBillingItems(
  orderIds: string[]
): Promise<Record<string, BillingItem[]>> {
  return post<Record<string, BillingItem[]>, { orderIds: string[] }>(
    '/v1/billing-items/generate-batch',
    { orderIds }
  );
}

export async function createInvoice(
  prtId: string,
  params: {
    billingItemIds: string[];
    invoiceDate: string;
    dueDate: string;
    rtuId: string;
  }
): Promise<Invoice> {
  return post<Invoice, typeof params>(`/v1/prt/${prtId}/invoices`, params);
}

export async function approveInvoice(
  prtId: string,
  invoiceId: string,
  approverId: string,
  comment?: string
): Promise<Invoice> {
  const url =
    `/v1/prt/${prtId}/invoices/${invoiceId}/approve?approverId=${approverId}` +
    (comment ? `&comment=${encodeURIComponent(comment)}` : '');
  return post<Invoice, undefined>(url);
}

export async function rejectInvoice(
  prtId: string,
  invoiceId: string,
  approverId: string,
  comment?: string
): Promise<Invoice> {
  const url =
    `/v1/prt/${prtId}/invoices/${invoiceId}/reject?approverId=${approverId}` +
    (comment ? `&comment=${encodeURIComponent(comment)}` : '');
  return post<Invoice, undefined>(url);
}

export async function getApprovalThreshold(): Promise<number> {
  const response = await get<{ threshold: number }>(
    `/v1/config/approval-threshold`
  );
  return response.threshold;
}

export async function approveBillingItem(
  billingItemId: string,
  approverId: string,
  comment?: string
): Promise<BillingItem> {
  const url =
    `/v1/billing-items/${billingItemId}/approve?approverId=${approverId}` +
    (comment ? `&comment=${encodeURIComponent(comment)}` : '');
  return post<BillingItem, undefined>(url);
}

export async function approveBillingItemsBulk(
  billingItemIds: string[],
  approverId: string,
  comment?: string
): Promise<BillingItem[]> {
  return post<
    BillingItem[],
    {
      billingItemIds: number[];
      approverId: string;
      comment?: string;
    }
  >('/v1/billing-items/approve-bulk', {
    billingItemIds: billingItemIds.map((id) => Number(id)),
    approverId,
    comment
  });
}

export async function rejectBillingItem(
  billingItemId: string,
  userId: string,
  comment?: string
): Promise<BillingItem> {
  const url =
    `/v1/billing-items/${billingItemId}/reject?userId=${userId}` +
    (comment ? `&comment=${encodeURIComponent(comment)}` : '');
  return post<BillingItem, undefined>(url);
}

export async function rejectBillingItemsBulk(
  billingItemIds: string[],
  userId: string,
  comment?: string
): Promise<BillingItem[]> {
  return post<
    BillingItem[],
    {
      billingItemIds: number[];
      userId: string;
      comment?: string;
    }
  >('/v1/billing-items/reject-bulk', {
    billingItemIds: billingItemIds.map((id) => Number(id)),
    userId,
    comment
  });
}

export async function reviewBillingItem(
  billingItemId: string,
  reviewerId: string,
  comment?: string
): Promise<BillingItem> {
  const url =
    `/v1/billing-items/${billingItemId}/review?reviewerId=${reviewerId}` +
    (comment ? `&comment=${encodeURIComponent(comment)}` : '');
  return post<BillingItem, undefined>(url);
}

export async function reviewBillingItemsBulk(
  billingItemIds: string[],
  reviewerId: string,
  comment?: string
): Promise<BillingItem[]> {
  return post<
    BillingItem[],
    {
      billingItemIds: number[];
      reviewerId: string;
      comment?: string;
    }
  >('/v1/billing-items/review-bulk', {
    billingItemIds: billingItemIds.map((id) => Number(id)),
    reviewerId,
    comment
  });
}

export async function fetchBillingItemsByOrder(
  orderId: string
): Promise<BillingItem[]> {
  return get<BillingItem[]>(`/v1/billing-items/order/${orderId}`);
}

export type InvoiceCorrectionDto = {
  invoiceCorrectionId: number;
  originalInvoiceId: number;
  correctionInvoiceId: number;
  correctionReason: string;
  correctionAmount: number;
  creationDate: string;
  createdBy: string;
};

export async function fetchInvoiceCorrections(invoiceId: number) {
  return get<InvoiceCorrectionDto[]>(
    `/v1/invoice-corrections/for-invoice/${invoiceId}`
  );
}

export async function createInvoiceCorrection(params: {
  originalInvoiceId: number;
  correctionInvoiceId: number;
  reason: string;
  amount: number;
  createdBy: string;
}) {
  const { originalInvoiceId, correctionInvoiceId, reason, amount, createdBy } =
    params;
  return post<InvoiceCorrectionDto, undefined>(
    `/v1/invoice-corrections?originalInvoiceId=${originalInvoiceId}&correctionInvoiceId=${correctionInvoiceId}&reason=${encodeURIComponent(
      reason
    )}&amount=${amount}&createdBy=${createdBy}`
  );
}

export async function fetchEligibleCorrectionInvoices(
  prtId: string,
  excludeInvoiceId: number
): Promise<Invoice[]> {
  return get<Invoice[]>(
    `/v1/prt/${prtId}/invoices/eligible-corrections?excludeInvoiceId=${excludeInvoiceId}`
  );
}

export async function fetchInvoiceById(
  prtId: string,
  invoiceId: number | string
): Promise<Invoice> {
  return get<Invoice>(`/v1/prt/${prtId}/invoices/${invoiceId}`);
}

function getAuthToken() {
  return localStorage.getItem('token');
}

export async function finalizeInvoice(prtId: string, invoiceId: string) {
  const token = getAuthToken();
  const url = API_CONFIG.getFullUrl(
    `/v1/prt/${prtId}/invoices/${invoiceId}/status?newStatus=FINALIZED`
  );
  return fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    credentials: 'include'
  }).then((res) => {
    if (!res.ok) throw new Error('Failed to finalize invoice');
    return res.json();
  });
}

export async function sendInvoice(prtId: string, invoiceId: string) {
  const token = getAuthToken();
  const url = API_CONFIG.getFullUrl(
    `/v1/prt/${prtId}/invoices/${invoiceId}/status?newStatus=SENT`
  );
  return fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    credentials: 'include'
  }).then((res) => {
    if (!res.ok) throw new Error('Failed to send invoice');
    return res.json();
  });
}

export async function emailInvoice(
  prtId: string,
  invoiceId: string,
  formData: FormData
) {
  const token = getAuthToken();
  const url = API_CONFIG.getFullUrl(
    `/v1/prt/${prtId}/invoices/${invoiceId}/email`
  );
  return fetch(url, {
    method: 'POST',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    body: formData,
    credentials: 'include'
  }).then((res) => {
    if (!res.ok) throw new Error('Failed to email invoice');
    return;
  });
}
